import React, { useState, useEffect } from "react";
import axios from "axios";
import { Footprints, Plus, CheckCircle2, RotateCcw, Phone, UserPlus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import Layout from "../components/Layout";
import DynamicFormRenderer from "../components/DynamicFormRenderer";
import { API } from "../App";
import { toast } from "sonner";

const WalkInPage = () => {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [recentEntries, setRecentEntries] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    axios.get(`${API}/projects`).then(r => setProjects(r.data)).catch(() => {});
  }, []);

  const resetForm = () => {
    setForm({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim() || !form.phone?.trim()) {
      toast.error("Name and phone are required");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        source: "walk_in",
        budget_min: form.budget_min ? parseFloat(form.budget_min) : undefined,
        budget_max: form.budget_max ? parseFloat(form.budget_max) : undefined,
      };

      // Remove empty strings
      Object.keys(payload).forEach(key => {
        if (payload[key] === "") delete payload[key];
      });

      const res = await axios.post(`${API}/leads`, payload);
      toast.success(`Lead captured: ${form.name}`);
      setRecentEntries(prev => [{ name: form.name, phone: form.phone, lead_id: res.data.lead_id, time: new Date() }, ...prev.slice(0, 9)]);
      resetForm();
    } catch (error) {
      toast.error("Failed to capture lead");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto space-y-6" data-testid="walkin-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
            <Footprints className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Walk-in Quick Entry</h1>
            <p className="text-slate-500">Rapid lead capture for reception desk</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form */}
          <div className="lg:col-span-2">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <DynamicFormRenderer
                    formType="walk_in"
                    values={form}
                    onChange={(field, value) => setForm(prev => ({ ...prev, [field]: value }))}
                    projects={projects}
                    showGroups={true}
                    gridCols={2}
                  />

                  <div className="flex gap-3 pt-2">
                    <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800 h-12 text-base" disabled={loading} data-testid="walkin-submit-btn">
                      <Plus className="w-5 h-5 mr-2" /> {loading ? "Saving..." : "Capture Lead"}
                    </Button>
                    <Button type="button" variant="outline" className="h-12" onClick={resetForm} data-testid="walkin-reset-btn">
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Recent Entries */}
          <div>
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold font-['Manrope']">Recent Walk-ins ({recentEntries.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {recentEntries.length > 0 ? (
                  <div className="space-y-3">
                    {recentEntries.map((entry, i) => (
                      <div key={i} className="flex items-center gap-3 p-2.5 bg-emerald-50 rounded-lg" data-testid={`recent-walkin-${i}`}>
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 text-sm truncate">{entry.name}</p>
                          <p className="text-xs text-slate-500">{entry.phone}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 py-6 text-center">Walk-ins captured today will appear here</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default WalkInPage;
